import { useRef, useState } from "react";
import { AnimatePresence, motion, useMotionValueEvent, useScroll } from "framer-motion";
import { useExplorer } from "@/context/ExplorerContext";
import { ImageSlot } from "@/components/lurio/ImageSlot";
import { Eyebrow, Hand, Fr } from "@/components/lurio/Primitives";
import { EASE } from "@/components/lurio/Motion";

const IS_DEV = process.env.NODE_ENV !== "production";

// ?page=<spread id, e.g. "code"> or ?page=<1-based number, e.g. "4"> — dev/QA only, see DevPageJump below.
function pageParamIndex(spreads) {
  if (!IS_DEV) return null;
  const raw = new URLSearchParams(window.location.search).get("page");
  if (!raw) return null;
  const byId = spreads.findIndex((s) => s.id === raw);
  if (byId !== -1) return byId;
  const n = Number(raw);
  return Number.isInteger(n) && n >= 1 && n <= spreads.length ? n - 1 : null;
}

// Dev-only: jump straight to any page without scrolling the full section. Stripped from
// production builds entirely (IS_DEV is a build-time constant, so bundlers dead-code-eliminate
// this whole branch — see the IS_DEV check in InsideBook below).
const DevPageJump = ({ spreads, idx, onJump }) => (
  <div dir="ltr" data-testid="dev-page-jump" className="fixed bottom-4 start-4 z-[100] flex max-w-[90vw] flex-wrap gap-1 rounded-lg bg-ink/90 p-2 font-sans text-xs text-cream shadow-lg">
    <span className="px-1 py-1 font-bold opacity-70">dev · jump:</span>
    {spreads.map((s, i) => (
      <button
        key={s.id}
        type="button"
        data-testid={`dev-page-jump-${s.id}`}
        onClick={() => onJump(i)}
        className={`rounded px-2 py-1 font-bold transition-colors ${i === idx ? "bg-coral text-cream" : "bg-cream/10 hover:bg-cream/20"}`}
      >
        {i + 1}. {s.id}
      </button>
    ))}
  </div>
);

// Position of the printed name box on page 1 (percent of page size), so the preview sits inside the real frame.
const NAME_BOX = { left: "7%", top: "35.4%", width: "86%", height: "9.6%" };

const Page = ({ spread, name, t }) => (
  <motion.div
    className="absolute inset-0"
    initial={{ opacity: 0, x: -40, rotateY: 8 }}
    animate={{ opacity: 1, x: 0, rotateY: 0 }}
    exit={{ opacity: 0, x: 40, rotateY: -8 }}
    transition={{ duration: 0.3, ease: EASE }}
    style={{ transformPerspective: 1200 }}
  >
    <ImageSlot id={spread.slot} rounded="rounded-[14px]" className="h-full w-full ring-1 ring-ink/10 shadow-[0_40px_80px_-40px_rgba(30,42,56,0.55)]">
      {spread.id === "ownership" && (
        <div data-testid="ownership-overlay" className="absolute flex items-center justify-center" style={NAME_BOX}>
          <p className="font-hand leading-none text-blue" style={{ fontSize: "clamp(1.3rem, 9cqw, 3.6rem)" }}>
            <span data-testid="ownership-name">{name || t.namePrompt.fallback}</span>
          </p>
        </div>
      )}
    </ImageSlot>
  </motion.div>
);

export default function InsideBook() {
  const { t, display } = useExplorer();
  const ref = useRef(null);
  const spreads = t.inside.spreads;
  // Dev-only: a ?page= link or a DevPageJump click should still show the requested page once you
  // scroll down to the section, not get immediately stomped by the scroll handler below computing
  // idx=0 while you're still above it (scrollYProgress sits at 0 for the whole page until the
  // section's top reaches the viewport). Cleared for good the first time real scrolling *within*
  // the section is detected, so normal scroll-driven paging takes back over from then on.
  const pinnedRef = useRef(IS_DEV);
  const [idx, setIdx] = useState(() => pageParamIndex(spreads) ?? 0);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end end"] });
  useMotionValueEvent(scrollYProgress, "change", (v) => {
    if (pinnedRef.current) {
      if (v < 0.001) return;
      pinnedRef.current = false;
    }
    setIdx(Math.min(spreads.length - 1, Math.floor(v * spreads.length)));
  });
  // The overlay is position:fixed, so it's clickable from anywhere on the page (e.g. still up at
  // Hero) -- re-arm the same pin so the jump survives scrolling the rest of the way down to the
  // section, instead of getting overwritten the moment a scroll tick computes v=0 along the way.
  const jumpTo = (i) => {
    pinnedRef.current = true;
    setIdx(i);
  };
  const current = spreads[idx];

  return (
    <section id="livre" ref={ref} data-testid="section-inside-book" className="relative" style={{ height: `calc(${(spreads.length - 1) * 70}vh + 100svh)` }}>
      <div className="sticky top-0 flex h-[100svh] flex-col justify-center overflow-hidden px-5 py-8 sm:px-8 lg:px-12">
        <div className="mx-auto grid w-full max-w-6xl items-center gap-6 lg:grid-cols-12 lg:gap-10">
          <div className="lg:col-span-5">
            <Eyebrow className="mb-3">{t.inside.eyebrow}</Eyebrow>
            <h2 className="font-serif text-3xl font-bold leading-[1.3] sm:leading-[1.3] lg:leading-[1.3] text-ink sm:text-4xl lg:text-5xl">{t.inside.title}</h2>
            <p className="mt-3 hidden text-sm font-semibold text-ink-soft lg:block">{t.inside.hint} ↓</p>
            <ol className="mt-6 hidden space-y-2.5 lg:block" aria-label="الصفحات">
              {spreads.map((s, i) => (
                <li key={s.id} data-testid={`spread-step-${s.id}`} className={`flex items-center gap-3 text-[0.95rem] transition-colors duration-300 ${i === idx ? "font-bold text-blue" : "text-ink/45"}`}>
                  <span className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-coral" : "w-3 bg-ink/20"}`} />
                  <span>{s.label} · <Fr>{s.printed}</Fr></span>
                </li>
              ))}
            </ol>
            <div className="mt-6 hidden lg:block">
              <Hand key={current.id} className="text-2xl" rotate={-2}>{current.note}</Hand>
            </div>
          </div>
          <div className="relative lg:col-span-7">
            <div className="relative mx-auto max-h-[58svh] [container-type:inline-size] lg:max-h-[74svh]" style={{ aspectRatio: "210 / 297" }}>
              {/* mode="sync" (the default) crossfades in/out together instead of waiting for the
                  exit to finish first -- with mode="wait" here, each page turn took up to 1s
                  (0.3s now) end to end before the next page was fully visible. */}
              <AnimatePresence mode="sync">
                <Page key={current.id} spread={current} name={display} t={t} />
              </AnimatePresence>
            </div>
            <div className="mt-4 flex items-start justify-between gap-4 lg:hidden">
              <p data-testid="spread-label" className="text-sm font-bold text-blue">{current.label} · <Fr>{current.printed}</Fr></p>
              <Hand key={current.id} className="max-w-[55%] text-end text-xl leading-tight" rotate={-2}>{current.note}</Hand>
            </div>
            <div className="mt-3 flex gap-1.5 lg:hidden" aria-hidden>
              {spreads.map((s, i) => (
                <span key={s.id} className={`h-1.5 rounded-full transition-all duration-300 ${i === idx ? "w-8 bg-coral" : "w-3 bg-ink/20"}`} />
              ))}
            </div>
          </div>
        </div>
      </div>
      {IS_DEV && <DevPageJump spreads={spreads} idx={idx} onJump={jumpTo} />}
    </section>
  );
}
